import React, { useEffect, useMemo, useState } from "react";
import { Activity, Cable, RadioTower, Trash2 } from "lucide-react";
import { io } from "socket.io-client";
import {
  connectWebSerial,
  getWebSerialStatus,
  onWebSerialData,
  onWebSerialStatus,
} from "../services/webSerial";
import FrameInspector from "./FrameInspector";
import { SUBSYS, SUBSYSTEM_NAME } from "../protocol/frameProtocol";
import "./OperationalViews.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";
const WINDOW_MS = 5000;
const MAX_EVENTS = 100;

function byteLength(value = "") {
  return Math.ceil(String(value).replace(/[^a-fA-F0-9]/g, "").length / 2);
}

function textLength(value = "") {
  return new Blob([String(value)]).size;
}

function makeEvent(direction, label, payload = {}, options = {}) {
  const hex = String(payload.hex || payload.frameHex || "").toUpperCase();
  const text = payload.data || payload.error || "";
  return {
    id: `${Date.now()}-${Math.random()}`,
    timestamp: Date.now(),
    direction,
    label,
    hex,
    text,
    bytes: options.countBytes === false ? 0 : (hex ? byteLength(hex) : textLength(text)),
    frameHex: payload.frameHex || (hex.startsWith("7E") ? hex : ""),
  };
}

function summarize(events, now) {
  const recent = events.filter((event) => now - event.timestamp <= WINDOW_MS);
  const tx = recent.filter((event) => event.direction === "TX");
  const rx = recent.filter((event) => event.direction === "RX");
  return {
    txRate: tx.reduce((total, event) => total + event.bytes, 0) / (WINDOW_MS / 1000),
    rxRate: rx.reduce((total, event) => total + event.bytes, 0) / (WINDOW_MS / 1000),
    txCount: events.filter((event) => event.direction === "TX").length,
    rxCount: events.filter((event) => event.direction === "RX").length,
    errorCount: events.filter((event) => event.direction === "ERROR").length,
  };
}

function formatTime(timestamp) {
  return new Date(timestamp).toLocaleTimeString("es-MX", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });
}

function MetricCard({ label, value, accent }) {
  return (
    <div className={`dashboard-metric ${accent || ""}`}>
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}

export default function Dashboard() {
  const [activeChannel, setActiveChannel] = useState("slave-master");
  const [slaveEvents, setSlaveEvents] = useState([]);
  const [guiEvents, setGuiEvents] = useState([]);
  const [backendStatus, setBackendStatus] = useState({ connected: false });
  const [webStatus, setWebStatus] = useState(getWebSerialStatus());
  const [message, setMessage] = useState("");
  const [now, setNow] = useState(Date.now());
  const [nodes, setNodes] = useState({});

  const recordNode = (payload, source) => {
    if (payload?.subsys === undefined) return;
    setNodes((current) => ({
      ...current,
      [payload.subsys]: {
        source,
        commandName: payload.commandName || "TRAMA",
        seq: payload.seq,
        receivedAt: Date.now(),
      },
    }));
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const append = (event) => setSlaveEvents((current) => [event, ...current].slice(0, MAX_EVENTS));
    const socket = io(API_BASE, { transports: ["websocket", "polling"] });

    socket.on("serial:status", (status) => setBackendStatus(status));
    socket.on("serial:data", (payload) => append(makeEvent("RX", `RX ${payload.path || "UART"}`, payload)));
    socket.on("serial:tx", (payload) => append(makeEvent("TX", payload.label || `TX ${payload.path || "UART"}`, payload)));
    socket.on("serial:frame", (payload) => {
      recordNode(payload, "Backend UART");
      append(makeEvent("FRAME", `${payload.commandName || "TRAMA"} · SEQ ${payload.seq}`, payload, { countBytes: false }));
    });
    socket.on("serial:frame-error", (payload) => append(makeEvent("ERROR", "Trama inválida", payload, { countBytes: false })));
    socket.on("serial:error", (payload) => append(makeEvent("ERROR", "Error UART", payload, { countBytes: false })));

    return () => socket.disconnect();
  }, []);

  useEffect(() => onWebSerialStatus(setWebStatus), []);

  useEffect(() => onWebSerialData((payload) => {
    const direction = payload.direction === "tx"
      ? "TX"
      : payload.direction === "rx"
        ? "RX"
        : payload.direction === "frame"
          ? "FRAME"
          : "ERROR";
    const label = payload.label
      || payload.commandName
      || (direction === "RX" ? "Datos recibidos" : direction === "TX" ? "Datos enviados" : payload.direction);
    const countBytes = direction === "TX" || direction === "RX";
    setGuiEvents((current) => [
      makeEvent(direction, label, payload, { countBytes }),
      ...current,
    ].slice(0, MAX_EVENTS));
    if (payload.direction === "frame") recordNode(payload, "Web Serial");
  }), []);

  const slaveSummary = useMemo(() => summarize(slaveEvents, now), [slaveEvents, now]);
  const guiSummary = useMemo(() => summarize(guiEvents, now), [guiEvents, now]);
  const events = activeChannel === "slave-master" ? slaveEvents : guiEvents;
  const summary = activeChannel === "slave-master" ? slaveSummary : guiSummary;

  const connectLocal = async () => {
    try {
      await connectWebSerial({ baudRate: 115200 });
      setMessage("Tarjeta local conectada a 115200 baudios.");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const clearActive = () => {
    if (activeChannel === "slave-master") setSlaveEvents([]);
    else setGuiEvents([]);
  };

  return (
    <div className="dashboard-view">
      <section className="ops-hero dashboard-hero">
        <div>
          <p className="ops-eyebrow">Resumen de enlace en tiempo real</p>
          <h3><Activity size={25} /> Dashboard</h3>
          <p>Monitorea las tasas TX/RX y revisa exactamente qué bytes se enviaron o recibieron en cada enlace.</p>
        </div>
        <div className="dashboard-status-stack">
          <span className={`ops-link-state ${backendStatus.connected ? "is-connected" : ""}`}>
            <i /> UART backend {backendStatus.connected ? "conectado" : "sin conexión"}
          </span>
          <span className={`ops-link-state ${webStatus.connected ? "is-connected" : ""}`}>
            <i /> Web Serial {webStatus.connected ? "conectado" : "sin conexión"}
          </span>
        </div>
      </section>

      <div className="dashboard-channel-grid">
        <button
          type="button"
          className={`dashboard-channel ${activeChannel === "slave-master" ? "is-active" : ""}`}
          onClick={() => setActiveChannel("slave-master")}
        >
          <RadioTower size={24} />
          <span><strong>Esclavos ↔ Maestro</strong><small>UART administrado por el backend</small></span>
          <b>{slaveSummary.txRate.toFixed(1)} / {slaveSummary.rxRate.toFixed(1)} B/s</b>
        </button>
        <button
          type="button"
          className={`dashboard-channel ${activeChannel === "master-gui" ? "is-active" : ""}`}
          onClick={() => setActiveChannel("master-gui")}
        >
          <Cable size={24} />
          <span><strong>Maestro ↔ GUI</strong><small>Web Serial en la computadora local</small></span>
          <b>{guiSummary.txRate.toFixed(1)} / {guiSummary.rxRate.toFixed(1)} B/s</b>
        </button>
      </div>

      <section className="dashboard-metrics">
        <MetricCard label="Tasa TX · últimos 5 s" value={`${summary.txRate.toFixed(1)} B/s`} accent="is-tx" />
        <MetricCard label="Tasa RX · últimos 5 s" value={`${summary.rxRate.toFixed(1)} B/s`} accent="is-rx" />
        <MetricCard label="Envíos registrados" value={summary.txCount} />
        <MetricCard label="Recepciones registradas" value={summary.rxCount} />
        <MetricCard label="Errores" value={summary.errorCount} accent={summary.errorCount ? "is-error" : ""} />
      </section>

      <section className="ops-card">
        <div className="ops-section-heading">
          <div><strong>Estado general de la OBC</strong><small>Actividad observada; no inventa estados que el firmware todavía no reporta.</small></div>
        </div>
        <div className="dashboard-metrics">
          {Object.values(SUBSYS).map((address) => {
            const node = nodes[address];
            return <MetricCard
              key={address}
              label={`${SUBSYSTEM_NAME[address]} · SUB 0x${address.toString(16).padStart(2, "0").toUpperCase()}`}
              value={node ? `${node.commandName} · SEQ ${node.seq}` : address === SUBSYS.MASTER ? "Firmware pendiente" : "Sin datos"}
              accent={node ? "is-rx" : ""}
            />;
          })}
        </div>
      </section>

      <section className="ops-card dashboard-log-card">
        <div className="ops-section-heading dashboard-log-heading">
          <div>
            <strong>{activeChannel === "slave-master" ? "Tráfico Esclavos ↔ Maestro" : "Tráfico Maestro ↔ GUI"}</strong>
            <small>Los eventos FRAME ayudan a interpretar una recepción y no duplican la tasa de bytes.</small>
          </div>
          <div className="ops-actions compact">
            {activeChannel === "master-gui" && !webStatus.connected && (
              <button type="button" onClick={connectLocal}><Cable size={15} /> Conectar tarjeta</button>
            )}
            <button type="button" onClick={clearActive}><Trash2 size={15} /> Limpiar</button>
          </div>
        </div>

        {message && <p className="ops-message">{message}</p>}
        <div className="dashboard-log" role="log" aria-live="polite">
          {events.length === 0 ? (
            <div className="dashboard-empty">Aún no hay tráfico registrado en este enlace.</div>
          ) : events.map((event) => (
            <div className={`dashboard-event is-${event.direction.toLowerCase()}`} key={event.id}>
              <time>{formatTime(event.timestamp)}</time>
              <span className="dashboard-direction">{event.direction}</span>
              <strong>{event.label}</strong>
              <code title={event.hex || event.text}>{event.hex || event.text || "—"}</code>
              <small>{event.bytes ? `${event.bytes} B` : "info"}</small>
              {event.frameHex && <FrameInspector hex={event.frameHex} direction={event.direction === "FRAME" ? "RX" : event.direction} label={event.label} />}
            </div>
          ))}
        </div>
      </section>

      <p className="ops-note dashboard-payload-note">
        La imagen reconstruida no se duplica aquí: continúa en la vista Payload, junto con los comandos Umbral y Sobel.
      </p>
    </div>
  );
}
